import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowRight, Sparkles } from 'lucide-react';

/**
 * QuickLinkPickerDialog — extracted from BankingQuickLinkPage.jsx (iter399).
 *
 * Renders the entity / subtype picker that opens when the user clicks one
 * of the outgoing-purpose or incoming-purpose chips on a Quick-Link row.
 *
 * Two modes:
 *   - Entity mode (pickerType.endpoint truthy): renders a search input + a
 *     scrollable list of `filteredOptions` (suppliers / customers /
 *     employees / partners / platforms / expense-categories). Clicking a
 *     row calls `submitLink(option)`.
 *   - Tag-only mode (pickerType.endpoint falsy): renders a friendly hint
 *     card and a single "Save link" footer button that submits with
 *     `{id: null, name: pickerType.label}` so government / refund / other-
 *     income flows don't need an entity to choose.
 *
 * All state is held by the parent page; this component is purely
 * presentational — it forwards every interaction back via props.
 */
export function QuickLinkPickerDialog({
  pickerType,
  setPickerType,
  SUBTYPES,
  pickerSubtype,
  setPickerSubtype,
  branches,
  pickerBranchId,
  setPickerBranchId,
  pickerDescription,
  setPickerDescription,
  pickerSearch,
  setPickerSearch,
  pickerLoading,
  suggestedTop,
  filteredOptions,
  submitLink,
  submitting,
}) {
  const stKey = pickerType?.mapTo || pickerType?.key;
  const stList = SUBTYPES[stKey];

  return (
    <Dialog open={!!pickerType} onOpenChange={(v) => { if (!v) setPickerType(null); }}>
      <DialogContent className="max-w-md" data-testid="qlink-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {pickerType ? <pickerType.icon size={18} className="text-violet-600" /> : null}
            Pick a {pickerType?.label.toLowerCase() || ''}
          </DialogTitle>
          <DialogDescription>
            The system will create a {pickerType?.label.toLowerCase()} record linked to this bank payment, and the row will leave your inbox.
          </DialogDescription>
        </DialogHeader>

        {/* Subtype dropdown (only when the entity_type has a SUBTYPES list). */}
        {stList && stList.length > 0 && (
          <div className="space-y-2">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Type
              </label>
              <select
                value={pickerSubtype}
                onChange={(e) => setPickerSubtype(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm"
                data-testid="qlink-picker-subtype"
              >
                {stList.map((s) => (
                  <option key={s.value} value={s.value} title={s.help || ''}>
                    {s.label}{s.help ? ` — ${s.help}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {branches.length > 1 && (
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Paying branch
              <span className="ml-1 normal-case font-normal text-muted-foreground/70">— change if a different branch paid for this</span>
            </label>
            <select
              value={pickerBranchId}
              onChange={(e) => setPickerBranchId(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm"
              data-testid="qlink-picker-branch"
            >
              <option value="">(Default — from bank payment)</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Description / Notes
            <span className="ml-1 normal-case font-normal text-muted-foreground/70">— shown on the downstream record</span>
          </label>
          <Input
            value={pickerDescription}
            onChange={(e) => setPickerDescription(e.target.value)}
            placeholder="e.g. Monthly office rent — March"
            className="mt-1"
            data-testid="qlink-picker-description"
          />
        </div>

        <Input
          value={pickerSearch}
          onChange={(e) => setPickerSearch(e.target.value)}
          placeholder="Search by name…"
          autoFocus
          data-testid="qlink-picker-search"
        />
        <div className="max-h-[340px] overflow-y-auto rounded-md border" data-testid="qlink-picker-list">
          {!pickerLoading && suggestedTop.length > 0 && (
            <div className="bg-emerald-50 border-b" data-testid="qlink-suggested-section">
              <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
                <Sparkles size={11} /> Already known on this account
                <span className="ml-auto text-[9px] text-emerald-600 font-normal normal-case">verify before linking</span>
              </div>
              {suggestedTop.map((o) => (
                <button
                  key={`sug-${o.id}`}
                  type="button"
                  onClick={() => submitLink(o)}
                  disabled={submitting}
                  data-testid={`qlink-suggested-${o.id}`}
                  className="w-full flex items-center justify-between text-left px-3 py-2 border-t border-emerald-100 hover:bg-emerald-100/60 disabled:opacity-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                      {o.name}
                      {o.confidence && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                          o.confidence === 'high' ? 'bg-success/20 text-success' :
                          o.confidence === 'medium' ? 'bg-warning/20 text-warning' :
                          'bg-muted text-muted-foreground'
                        }`}>{(o.confidence || '').toUpperCase()}</span>
                      )}
                    </div>
                    {o.sub ? <div className="text-[10px] text-muted-foreground">{o.sub}</div> : null}
                  </div>
                  <ArrowRight size={14} className="text-emerald-600 shrink-0 ml-2" />
                </button>
              ))}
            </div>
          )}
          {pickerLoading ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
          ) : (pickerType && !pickerType.endpoint) ? (
            <div className="p-5 text-center space-y-2 bg-rose-50/40 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 rounded-lg">
              <div className="text-sm font-semibold text-rose-900 dark:text-rose-200">{pickerType.label}</div>
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                This row will be tagged as <b>{pickerType.label}</b>{pickerSubtype ? <> — <b>{(SUBTYPES[pickerType.mapTo || pickerType.key] || []).find((s) => s.value === pickerSubtype)?.label || pickerSubtype}</b></> : null}.
                {' '}Pick the subtype above (if shown) and click <b>Save link</b> below to create the categorized expense.
              </div>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">No matches.</div>
          ) : (
            filteredOptions.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => submitLink(o)}
                disabled={submitting}
                data-testid={`qlink-picker-option-${o.id}`}
                className="w-full flex items-center justify-between text-left px-3 py-2 border-b last:border-b-0 hover:bg-violet-50 disabled:opacity-50 transition-colors"
              >
                <div>
                  <div className="text-sm font-semibold">{o.name}</div>
                  {o.sub ? <div className="text-[10px] text-muted-foreground">{o.sub}</div> : null}
                </div>
                <ArrowRight size={14} className="text-violet-600" />
              </button>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPickerType(null)} disabled={submitting}>Cancel</Button>
          {pickerType && !pickerType.endpoint && (
            <Button
              onClick={() => submitLink({ id: null, name: pickerType.label }, pickerType)}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="qlink-tag-only-save"
            >
              {submitting ? 'Saving…' : 'Save link'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QuickLinkPickerDialog;
